/**
 * PortalModal — create or edit a Permit Portal (Supabase).
 *
 * Opens via CustomEvent('open-portal-modal', { detail }):
 *   create: { permitId, permitAirtableId }
 *   edit:   { portalId, prefill: { jurisdiction_name, state, url, username, password, notes } }
 *
 * On success busts ctx.portalCache[permitAirtableId] and dispatches outline-render.
 */

import { useState, useEffect } from 'react'
import { ctx } from '../outline/ctx.js'
import { createPortal, updatePortal } from '../services/portals.js'

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}
const US_STATES = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming']

export default function PortalModal() {
  const [open,       setOpen]       = useState(false)
  const [portalId,   setPortalId]   = useState(null)
  const [permitId,   setPermitId]   = useState(null)        // Supabase permit UUID (unused for now)
  const [permitAtId, setPermitAtId] = useState(null)        // Airtable permit record id
  const [jurisd,     setJurisd]     = useState('')
  const [state,      setState]      = useState('')
  const [url,        setUrl]        = useState('')
  const [username,   setUsername]   = useState('')
  const [password,   setPassword]   = useState('')
  const [showPw,     setShowPw]     = useState(false)
  const [notes,      setNotes]      = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error,      setError]      = useState(null)

  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }
  }, [open])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setPortalId(d.portalId || null)
      setPermitId(d.permitId || null)
      setPermitAtId(d.permitAirtableId || null)
      const p = d.prefill || {}
      setJurisd(p.jurisdiction_name || '')
      setState(p.state || '')
      setUrl(p.url || '')
      setUsername(p.username || '')
      setPassword(p.password || '')
      setShowPw(false)
      setNotes(p.notes || '')
      setError(null); setSubmitting(false); setOpen(true)
    }
    window.addEventListener('open-portal-modal', onOpen)
    return () => window.removeEventListener('open-portal-modal', onOpen)
  }, [])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  async function handleSubmit() {
    setSubmitting(true); setError(null)
    const fields = { jurisdiction_name: jurisd || null, state: state || null, url: url || null, username: username || null, password: password || null, notes: notes || null }
    try {
      if (portalId) {
        await updatePortal(portalId, fields)
      } else {
        await createPortal(fields, permitAtId || undefined)
      }
      if (permitAtId && ctx.portalCache) ctx.portalCache[permitAtId] = undefined
      setOpen(false)
      window.dispatchEvent(new Event('outline-render'))
    } catch (ex) {
      setError(ex.message || 'Failed to save portal'); setSubmitting(false)
    }
  }

  if (!open) return null
  const canSubmit = !submitting
  const title = portalId ? 'Edit Portal' : 'Add Portal'

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div data-modal-id="PortalModal" style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 440, padding: '20px 20px 16px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)' }}>{title}</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 16, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Jurisdiction / Agency</label>
              <input style={inputStyle} value={jurisd} onChange={e => setJurisd(e.target.value)} placeholder="e.g. Newport Beach" />
            </div>
            <div style={{ width: 140 }}>
              <label style={labelStyle}>State</label>
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={state} onChange={e => setState(e.target.value)}>
                <option value="">— State —</option>
                {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Portal URL</label>
            <input style={inputStyle} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…" type="url" />
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Username / Login</label>
              <input style={inputStyle} value={username} onChange={e => setUsername(e.target.value)} placeholder="agencyreviews@alderjames.com" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input style={{ ...inputStyle, paddingRight: 36 }} value={password} onChange={e => setPassword(e.target.value)} type={showPw ? 'text' : 'password'} placeholder="••••••••" />
                <button type="button" onClick={() => setShowPw(v => !v)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 11, padding: 0 }}>
                  {showPw ? 'hide' : 'show'}
                </button>
              </div>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 52 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Security questions, special instructions…" />
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={() => setOpen(false)} style={{ padding: '6px 14px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>Cancel</button>
            <button onClick={handleSubmit} disabled={!canSubmit} style={{ padding: '6px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: canSubmit ? '#07272D' : 'var(--aj-bg-muted)', color: canSubmit ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: canSubmit ? 'pointer' : 'default' }}>
              {submitting ? 'Saving…' : (portalId ? 'Save' : 'Add Portal')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
